import { SessionStorageKeys } from '@consts';

type SessionStorageKey = (typeof SessionStorageKeys)[keyof typeof SessionStorageKeys];

export class SessionStorageService {
  private static instance: SessionStorageService;

  public static getInstance(): SessionStorageService {
    if (!SessionStorageService.instance) {
      SessionStorageService.instance = new SessionStorageService();
    }
    return SessionStorageService.instance;
  }

  private isClient = (): boolean => {
    return typeof window === 'object';
  };

  public readJson = (key: SessionStorageKey): unknown => {
    if (!this.isClient()) return null;

    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // Saving runs right before flows like a purchase, so a full or blocked storage must not stop them.
  public writeJson = (key: SessionStorageKey, value: unknown): void => {
    if (!this.isClient()) return;

    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // nothing is saved, and the caller goes on
    }
  };

  public remove = (key: SessionStorageKey): void => {
    if (!this.isClient()) return;

    try {
      sessionStorage.removeItem(key);
    } catch {
      // nothing to clean up if storage is unavailable
    }
  };
}

export const useSessionStorage = () => {
  const sessionStorageService = SessionStorageService.getInstance();

  return {
    readJson: sessionStorageService.readJson,
    writeJson: sessionStorageService.writeJson,
    remove: sessionStorageService.remove
  };
};
